import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Checkbox, Space, Typography, Popconfirm, message, Empty, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import type { TenantOrder } from '@/mocks/orders';
import {
  ORDER_TAG_COLORS,
  textColorFor,
  useOrderTags,
  useCreateOrderTag,
  useUpdateOrderTag,
  useDeleteOrderTag,
  useAssignOrderTags,
  type OrderTag,
} from '@/api/orders/order-tags';

interface OrderTagModalProps {
  order: TenantOrder | null;
  onClose: () => void;
}

const TAG_NAME_MAX = 255;

export function OrderTagModal({ order, onClose }: OrderTagModalProps) {
  const open = order !== null;
  const { data: tags = [], isLoading } = useOrderTags();
  const createTag = useCreateOrderTag();
  const updateTag = useUpdateOrderTag();
  const deleteTag = useDeleteOrderTag();
  const assignTags = useAssignOrderTags();

  const [view, setView] = useState<'list' | 'editor'>('list');
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState<string>(ORDER_TAG_COLORS[0]);

  useEffect(() => {
    if (!open || !order) return;
    setChecked(new Set(order.tags ?? []));
    setView('list');
    setEditingId(null);
    setSearch('');
  }, [open, order]);

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(q));
  }, [tags, search]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setDraftName('');
    setDraftColor(ORDER_TAG_COLORS[0]);
    setView('editor');
  };

  const openEdit = (tag: OrderTag) => {
    setEditingId(tag._id);
    setDraftName(tag.name);
    setDraftColor(tag.color);
    setView('editor');
  };

  const backToList = () => {
    setView('list');
    setEditingId(null);
  };

  const handleSaveTag = async () => {
    const name = draftName.trim();
    if (!name) {
      message.error('Vui lòng nhập tên thẻ');
      return;
    }
    if (name.length > TAG_NAME_MAX) {
      message.error(`Tên thẻ tối đa ${TAG_NAME_MAX} ký tự`);
      return;
    }
    try {
      if (editingId) {
        await updateTag.mutateAsync({ id: editingId, payload: { name, color: draftColor } });
        message.success('Đã cập nhật thẻ');
      } else {
        await createTag.mutateAsync({ name, color: draftColor });
        message.success('Đã tạo thẻ');
      }
      backToList();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Lưu thẻ thất bại');
    }
  };

  const handleDeleteTag = async (id: string) => {
    try {
      await deleteTag.mutateAsync(id);
      setChecked((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      message.success('Đã xoá thẻ');
    } catch {
      message.error('Xoá thẻ thất bại');
    }
  };

  const handleSubmit = async () => {
    if (!order) return;
    const initial = new Set(order.tags ?? []);
    const addTagIds = [...checked].filter((id) => !initial.has(id));
    const removeTagIds = [...initial].filter((id) => !checked.has(id));
    if (addTagIds.length === 0 && removeTagIds.length === 0) {
      message.warning('Chưa có thay đổi thẻ nào');
      return;
    }
    try {
      await assignTags.mutateAsync({ orderId: order._id, add_tag_ids: addTagIds, remove_tag_ids: removeTagIds });
      message.success('Đã cập nhật thẻ cho đơn hàng');
      onClose();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Cập nhật thẻ thất bại');
    }
  };

  if (!order) return null;

  if (view === 'editor') {
    return (
      <Modal title="Gắn thẻ đơn hàng" open={open} onCancel={onClose} footer={null} width={780}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Input
            placeholder={editingId ? 'Tên thẻ' : 'Thẻ mới'}
            maxLength={TAG_NAME_MAX}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            aria-label="Tên thẻ"
            size="large"
          />
          <Row gutter={[16, 16]}>
            {ORDER_TAG_COLORS.map((color) => (
              <Col span={6} key={color}>
                <button
                  type="button"
                  aria-label={`color-${color}`}
                  onClick={() => setDraftColor(color)}
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 8,
                    background: color,
                    border: draftColor === color ? '2px solid #1677ff' : '1px solid #d9d9d9',
                    cursor: 'pointer',
                    fontSize: 20,
                  }}
                >
                  {draftColor === color ? <span style={{ color: textColorFor(color) }}>✓</span> : null}
                </button>
              </Col>
            ))}
          </Row>
          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Button type="primary" onClick={handleSaveTag} loading={createTag.isPending || updateTag.isPending}>
              Xác nhận
            </Button>
            <Button onClick={backToList}>Hủy</Button>
          </Space>
        </Space>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Gắn thẻ đơn hàng ${order.order_number}`}
      open={open}
      onCancel={onClose}
      width={780}
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Hủy</Button>
          <Button type="primary" loading={assignTags.isPending} onClick={handleSubmit}>
            Cập nhật
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input placeholder="Tìm kiếm thẻ tag" value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Text strong>Các thẻ đã tạo: {tags.length}</Typography.Text>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Thêm thẻ tag
          </Button>
        </Space>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {isLoading ? (
            <Typography.Text type="secondary">Đang tải…</Typography.Text>
          ) : filteredTags.length === 0 ? (
            <Empty description="Chưa có thẻ nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Row gutter={[10, 8]}>
              {filteredTags.map((tag) => {
                const isChecked = checked.has(tag._id);
                const fg = textColorFor(tag.color);
                return (
                  <Col span={12} key={tag._id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 8px' }}>
                      <Checkbox checked={isChecked} onChange={() => toggle(tag._id)} aria-label={`chọn ${tag.name}`} />
                      <button
                        type="button"
                        onClick={() => toggle(tag._id)}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          background: tag.color,
                          color: fg,
                          border: isChecked ? '2px solid #1677ff' : '1px solid transparent',
                          borderRadius: 6,
                          padding: '6px 10px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {tag.name}
                      </button>
                      <Button size="small" type="text" icon={<EditOutlined />} aria-label={`sửa ${tag.name}`} onClick={() => openEdit(tag)} />
                      <Popconfirm title="Xoá thẻ này?" okText="Xoá" cancelText="Hủy" onConfirm={() => handleDeleteTag(tag._id)}>
                        <Button size="small" type="text" danger icon={<CloseOutlined />} aria-label={`xoá ${tag.name}`} />
                      </Popconfirm>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </Space>
    </Modal>
  );
}

export default OrderTagModal;
